import { useState, useEffect } from "react";
import { CustomSelect } from "@/components/CustomSelect";
import { DateInput } from "@/components/DateInput";
import { fmtDate } from "@/lib/dates";
import { Plus, CalendarClock, Trash2, Bell } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/Button";
import { Modal, Field, TextInput, TextArea } from "@/components/Modal";
import { EmptyState, Loader } from "@/components/common";

const DATE_CATS = ["Anniversary", "Birthday", "Due Date", "Renewal", "Appointment", "Trip", "Custom"];
const REMINDERS = [["none", "No reminder"], ["same_day", "Same day"], ["1_day", "1 day before"], ["3_days", "3 days before"], ["1_week", "1 week before"]];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  return Math.round((d - now) / 86400000);
}

export function DatesSection({ cid }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/connections/${cid}/dates`); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const startAdd = () => { setForm({ title: "", date: "", category: "Anniversary", notes: "" }); setOpen(true); };

  const save = async () => {
    if (!form.category?.trim() || form.category === "Custom") return toast.error("Choose a category (or type your custom one).");
    if (!form.date) return toast.error("Pick a date.");
    setBusy(true);
    try {
      await api.post(`/connections/${cid}/dates`, { title: form.title, date: form.date, category: form.category, notes: form.notes || "", reminders: {} });
      setOpen(false); await load(); toast.success("Date added");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const setReminder = async (d, val) => {
    await api.put(`/connections/${cid}/dates/${d.id}/reminder`, null, { params: { reminder: val } });
    await load();
  };
  const del = async (d) => { if (confirm("Delete this date?")) { await api.delete(`/connections/${cid}/dates/${d.id}`); await load(); } };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={startAdd} data-testid="add-date-btn"><Plus size={16} /> Add Date</Button></div>
      {items.length === 0 ? (
        <EmptyState icon={CalendarClock} title="No important dates" subtitle="Never miss an anniversary, birthday, or renewal again." action={<Button onClick={startAdd}><Plus size={16} /> Add Date</Button>} testId="dates-empty" />
      ) : (
        <div className="space-y-2">
          {items.map((d) => {
            const days = daysUntil(d.date);
            const myReminder = (d.reminders || {})[user.id] || "none";
            return (
              <div key={d.id} className="iks-card p-4 group" data-testid="date-card">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-sky-500/10 flex flex-col items-center justify-center shrink-0">
                    {days != null && days >= 0 ? (
                      <><span className="text-lg font-bold text-sky-400 leading-none">{days}</span><span className="text-[9px] text-slate-500 uppercase">{days === 0 ? "today" : "days"}</span></>
                    ) : <CalendarClock className="text-sky-400" size={22} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-400">{d.category}</span>
                    <p className="font-medium text-slate-100">{d.title}</p>
                    <p className="text-slate-500 text-xs">{fmtDate(d.date)}</p>
                  </div>
                  <button onClick={() => del(d)} className="p-1.5 text-slate-500 hover:text-red-400 opacity-60 group-hover:opacity-100 transition-opacity"><Trash2 size={15} /></button>
                </div>
                {d.notes && <p className="text-slate-400 text-sm mt-2 whitespace-pre-line break-words">{d.notes}</p>}
                <div className="flex items-center gap-2 mt-3">
                  <Bell size={13} className="text-slate-500" />
                  <select value={myReminder} onChange={(e) => setReminder(d, e.target.value)} className="iks-input px-2 py-1 text-xs" data-testid="reminder-select">
                    {REMINDERS.map(([v, l]) => <option key={v} value={v} className="bg-slate-900">{l}</option>)}
                  </select>
                  <span className="text-xs text-slate-600">your reminder</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add important date" testId="date-modal"
        footer={<><Button variant="secondary" full onClick={() => setOpen(false)}>Cancel</Button><Button full loading={busy} onClick={save} data-testid="save-date-btn">Save</Button></>}>
        <Field label="Title (optional)" hint="Leave blank to use the category as the title"><TextInput value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={form.category && form.category !== "Custom" ? form.category : "Our Anniversary"} data-testid="date-title" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 [&>*]:min-w-0">
          <Field label="Date"><DateInput value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="date-input" /></Field>
          <Field label="Category">
            <CustomSelect options={DATE_CATS} value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} placeholder="e.g. Gotcha Day" testId="date-category" />
          </Field>
        </div>
        <Field label="Notes"><TextArea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></Field>
      </Modal>
    </div>
  );
}
